import React, { CSSProperties, Fragment, HTMLAttributes, ReactNode, useContext } from 'react'
import { Box, TableCell, TableRow, Typography } from '@mui/material'
import { DataTableBodyCell } from './cells'
import { DataTableBackgroundContext } from './DataTableBackground'
import { StickyColumns } from './DataTable.sticky'
import { DataTableCellRenderer, DataTableLayoutColumn } from './DataTable.types'
import { DataTableVariantConfig } from './DataTable.variants'

export type DataTableRowDrag = {
    setNodeRef: (element: HTMLElement | null) => void
    style: CSSProperties
    isDragging: boolean
    handleProps: Record<string, unknown>
}

export type DataTableRowProps<TRow, TColumnId extends string> = {
    row: TRow
    rowId: string
    rowNumber: number
    isFirstRow: boolean
    isLastRow: boolean
    isExpanded: boolean
    columns: DataTableLayoutColumn<TRow, TColumnId>[]
    columnCount: number
    variantConfig: DataTableVariantConfig
    indexColumnWidth: number
    renderCell: DataTableCellRenderer<TRow, TColumnId>
    renderRowActions?: (row: TRow) => ReactNode
    renderExpandedContent?: (row: TRow) => ReactNode
    stickyColumns?: StickyColumns
    /** Holds expanded content in view while the row above it scrolls sideways. */
    stickyContentWidth?: number
    drag?: DataTableRowDrag
}

export const DataTableRow = <TRow, TColumnId extends string>({
    row,
    rowId,
    rowNumber,
    isFirstRow,
    isLastRow,
    isExpanded,
    columns,
    columnCount,
    variantConfig,
    indexColumnWidth,
    renderCell,
    renderRowActions,
    renderExpandedContent,
    stickyColumns = {},
    stickyContentWidth = 0,
    drag,
}: DataTableRowProps<TRow, TColumnId>) => {
    const backdrop = useContext(DataTableBackgroundContext)

    const { actionsColumnWidth, hasStripedRows, hasRoundedCorners, bodyTypographyVariant, cellPaddingX, cellPaddingY } =
        variantConfig

    const hasActionsColumn = !!renderRowActions
    const showIndexColumn = indexColumnWidth > 0

    const stripeColor = hasStripedRows
        ? rowNumber % 2 === 1
            ? 'figma.grayscale.table1'
            : 'figma.grayscale.table2'
        : undefined

    // A pinned cell cannot inherit the row's colour, and an unstriped row has none to give it.
    const cellBackground = drag?.isDragging ? 'figma.cellHighlight' : (stripeColor ?? backdrop)

    const hasRoundedTop = hasRoundedCorners && isFirstRow
    const hasRoundedBottom = hasRoundedCorners && isLastRow && !isExpanded

    return (
        <Fragment key={rowId}>
            <TableRow
                ref={drag?.setNodeRef}
                style={drag?.style}
                {...(drag?.handleProps as HTMLAttributes<HTMLTableRowElement>)}
                sx={{
                    bgcolor: drag?.isDragging ? 'figma.cellHighlight' : stripeColor,
                    ...(!!drag && {
                        cursor: drag.isDragging ? 'grabbing' : 'grab',
                        position: 'relative',
                        zIndex: drag.isDragging ? 1 : 0,
                    }),
                }}
            >
                {showIndexColumn && (
                    <DataTableBodyCell
                        width={`${indexColumnWidth}px`}
                        paddingX={cellPaddingX}
                        paddingY={cellPaddingY}
                        isFirstColumn
                        isFirstRow={hasRoundedTop}
                        isLastRow={hasRoundedBottom}
                        sticky={stickyColumns.index}
                        background={cellBackground}
                    >
                        <Typography variant={bodyTypographyVariant} color="figma.grayscale.textHier2">
                            {rowNumber}.
                        </Typography>
                    </DataTableBodyCell>
                )}
                {columns.map((column, columnIndex) => (
                    <DataTableBodyCell
                        key={column.id}
                        width={column.width}
                        align={column.align}
                        paddingX={cellPaddingX}
                        paddingY={cellPaddingY}
                        isFirstColumn={!showIndexColumn && columnIndex === 0}
                        isLastColumn={!hasActionsColumn && columnIndex === columns.length - 1}
                        isFirstRow={hasRoundedTop}
                        isLastRow={hasRoundedBottom}
                        sticky={columnIndex === 0 ? stickyColumns.firstColumn : undefined}
                        background={cellBackground}
                    >
                        {renderCell(row, column.id)}
                    </DataTableBodyCell>
                ))}
                {hasActionsColumn && (
                    <DataTableBodyCell
                        width={`${actionsColumnWidth}px`}
                        align="right"
                        paddingX={cellPaddingX}
                        paddingY={cellPaddingY}
                        isLastColumn
                        isFirstRow={hasRoundedTop}
                        isLastRow={hasRoundedBottom}
                        sticky={stickyColumns.actions}
                        background={cellBackground}
                    >
                        {renderRowActions(row)}
                    </DataTableBodyCell>
                )}
            </TableRow>
            {isExpanded && !!renderExpandedContent && (
                <TableRow sx={{ bgcolor: stripeColor }}>
                    <TableCell
                        colSpan={columnCount}
                        sx={{
                            p: 0,
                            borderBottom: 'none',
                            borderBottomLeftRadius: hasRoundedCorners && isLastRow ? '8px' : 0,
                            borderBottomRightRadius: hasRoundedCorners && isLastRow ? '8px' : 0,
                        }}
                    >
                        <DataTableBackgroundContext.Provider value={stripeColor ?? backdrop}>
                            {stickyContentWidth ? (
                                <Box sx={{ position: 'sticky', left: 0, width: stickyContentWidth }}>
                                    {renderExpandedContent(row)}
                                </Box>
                            ) : (
                                renderExpandedContent(row)
                            )}
                        </DataTableBackgroundContext.Provider>
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    )
}

export default DataTableRow
